import type { SvgIconComponent } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BadgeIcon from '@mui/icons-material/Badge';
import StairsIcon from '@mui/icons-material/Stairs';
import WorkIcon from '@mui/icons-material/Work';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PublicIcon from '@mui/icons-material/Public';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import KeyIcon from '@mui/icons-material/Key';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import HistoryIcon from '@mui/icons-material/History';
import PaletteIcon from '@mui/icons-material/Palette';
import CampaignIcon from '@mui/icons-material/Campaign';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PaymentsIcon from '@mui/icons-material/Payments';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SavingsIcon from '@mui/icons-material/Savings';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import GavelIcon from '@mui/icons-material/Gavel';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { PermAction } from '@/api/contracts';

export interface NavItem {
  path: string;
  labelKey: string;
  icon: SvgIconComponent;
  /** Hidden unless the user holds this permission. */
  doctype?: string;
  action?: PermAction;
  /** Hidden unless the user holds this role (System Manager pages have no doctype). */
  role?: string;
}

export interface NavSection {
  key: string;
  labelKey: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'home',
    labelKey: 'nav.dashboard',
    items: [
      { path: '/', labelKey: 'nav.dashboard', icon: DashboardIcon },
      { path: '/approvals', labelKey: 'nav.approvals', icon: FactCheckIcon },
      // The screen guide, served with the bundle. No doctype gates it: it
      // describes the product, not this workspace's records.
      { path: '/guide', labelKey: 'nav.guide', icon: MenuBookIcon },
    ],
  },
  {
    key: 'people',
    labelKey: 'nav.people',
    items: [
      { path: '/employees', labelKey: 'nav.employees', icon: BadgeOutlinedIcon, doctype: 'Employee', action: PermAction.Read },
      { path: '/compliance', labelKey: 'nav.compliance', icon: VerifiedUserIcon, doctype: 'Tracked Document', action: PermAction.Read },
    ],
  },
  {
    key: 'time',
    labelKey: 'nav.time',
    items: [
      { path: '/attendance', labelKey: 'nav.attendance', icon: EventAvailableIcon, doctype: 'Attendance', action: PermAction.Read },
      { path: '/shift-assignments', labelKey: 'nav.shift_assignments', icon: AccessTimeIcon, doctype: 'Shift Assignment', action: PermAction.Read },
      { path: '/weekly-roster', labelKey: 'nav.weekly_roster', icon: CalendarViewWeekIcon, doctype: 'Weekly Roster', action: PermAction.Read },
      { path: '/devices', labelKey: 'nav.devices', icon: FingerprintIcon, doctype: 'Biometric Device', action: PermAction.Read },
      { path: '/leave', labelKey: 'nav.leave', icon: BeachAccessIcon, doctype: 'Leave Application', action: PermAction.Read },
      { path: '/lifecycle', labelKey: 'nav.lifecycle', icon: SwapHorizIcon, doctype: 'Employee Boarding', action: PermAction.Read },
      { path: '/exit', labelKey: 'nav.exit', icon: LogoutIcon, doctype: 'Full and Final Statement', action: PermAction.Read },
    ],
  },
  {
    key: 'pay',
    labelKey: 'nav.pay',
    items: [
      { path: '/payroll', labelKey: 'nav.payroll', icon: PaymentsIcon, doctype: 'Payroll Entry', action: PermAction.Read },
      { path: '/salary-slips', labelKey: 'nav.salary_slips', icon: PaymentsIcon, doctype: 'Salary Slip', action: PermAction.Read },
      { path: '/salary-structures', labelKey: 'nav.salary_structures', icon: TuneIcon, doctype: 'Salary Structure', action: PermAction.Read },
      { path: '/payroll-settings', labelKey: 'nav.payroll_settings', icon: SettingsIcon, doctype: 'Payroll Settings', action: PermAction.Read },
      { path: '/expenses', labelKey: 'nav.expenses', icon: ReceiptLongIcon, doctype: 'Expense Claim', action: PermAction.Read },
      { path: '/benefits', labelKey: 'nav.benefits', icon: SavingsIcon, doctype: 'Employee Tax Exemption Declaration', action: PermAction.Read },
      { path: '/reports', labelKey: 'nav.reports', icon: AssessmentIcon },
    ],
  },
  {
    key: 'talent',
    labelKey: 'nav.talent',
    items: [
      { path: '/recruitment', labelKey: 'nav.recruitment', icon: WorkOutlineIcon, doctype: 'Job Opening', action: PermAction.Read },
      { path: '/applicants', labelKey: 'nav.applicants', icon: PersonSearchIcon, doctype: 'Job Applicant', action: PermAction.Read },
      { path: '/performance', labelKey: 'nav.performance', icon: TrendingUpIcon, doctype: 'Appraisal', action: PermAction.Read },
      { path: '/training', labelKey: 'nav.training', icon: SchoolIcon, doctype: 'Training Program', action: PermAction.Read },
    ],
  },
  {
    key: 'operations',
    labelKey: 'nav.operations',
    items: [
      { path: '/fleet', labelKey: 'nav.fleet', icon: LocalShippingIcon, doctype: 'Vehicle', action: PermAction.Read },
      { path: '/travel', labelKey: 'nav.travel', icon: FlightTakeoffIcon, doctype: 'Travel Request', action: PermAction.Read },
      { path: '/grievances', labelKey: 'nav.grievances', icon: GavelIcon, doctype: 'Employee Grievance', action: PermAction.Read },
      { path: '/workplace', labelKey: 'nav.workplace', icon: HealthAndSafetyIcon, doctype: 'Daily Work Summary', action: PermAction.Read },
    ],
  },
  {
    key: 'organization',
    labelKey: 'nav.organization',
    items: [
      { path: '/companies', labelKey: 'nav.companies', icon: BusinessIcon, doctype: 'Company', action: PermAction.Read },
      { path: '/branches', labelKey: 'nav.branches', icon: StorefrontIcon, doctype: 'Branch', action: PermAction.Read },
      { path: '/departments', labelKey: 'nav.departments', icon: AccountTreeIcon, doctype: 'Department', action: PermAction.Read },
      { path: '/designations', labelKey: 'nav.designations', icon: BadgeIcon, doctype: 'Designation', action: PermAction.Read },
      { path: '/employee-grades', labelKey: 'nav.grades', icon: StairsIcon, doctype: 'Employee Grade', action: PermAction.Read },
      { path: '/employment-types', labelKey: 'nav.employment_types', icon: WorkIcon, doctype: 'Employment Type', action: PermAction.Read },
      { path: '/administrations', labelKey: 'nav.administrations', icon: ApartmentIcon, doctype: 'Administration', action: PermAction.Read },
      { path: '/nationalities', labelKey: 'nav.nationalities', icon: PublicIcon, doctype: 'Nationality', action: PermAction.Read },
      { path: '/holiday-lists', labelKey: 'nav.holiday_lists', icon: EventIcon, doctype: 'Holiday List', action: PermAction.Read },
    ],
  },
  {
    key: 'access',
    labelKey: 'nav.access',
    items: [
      { path: '/users', labelKey: 'nav.users', icon: PeopleIcon, doctype: 'User', action: PermAction.Read },
      { path: '/roles', labelKey: 'nav.roles', icon: SecurityIcon, doctype: 'Role', action: PermAction.Read },
      { path: '/permissions', labelKey: 'nav.permissions', icon: KeyIcon, doctype: 'DocPermission', action: PermAction.Read },
    ],
  },
  {
    key: 'system',
    labelKey: 'nav.system',
    items: [
      { path: '/jobs', labelKey: 'nav.jobs', icon: ScheduleIcon, role: 'System Manager' },
      { path: '/data', labelKey: 'nav.data', icon: ImportExportIcon },
      {
        path: '/system-logs',
        labelKey: 'nav.system_logs',
        icon: HistoryIcon,
        doctype: 'Audit Log',
        action: PermAction.Read,
      },
      {
        path: '/notification-rules',
        labelKey: 'nav.notification_rules',
        icon: CampaignIcon,
        doctype: 'NotificationRule',
        action: PermAction.Read,
      },
      {
        path: '/workflows',
        labelKey: 'nav.workflows',
        icon: AccountTreeIcon,
        doctype: 'Workflow',
        action: PermAction.Read,
      },
      // The page reads four statutory tables; the tax slab is the one every
      // payslip needs, so it stands for the set.
      {
        path: '/statutory',
        labelKey: 'nav.statutory',
        icon: GavelIcon,
        doctype: 'Income Tax Slab',
        action: PermAction.Read,
      },
      { path: '/branding', labelKey: 'nav.branding', icon: PaletteIcon, role: 'System Manager' },
    ],
  },
];
